/**
 * React wrapper for Bryntum Scheduler
 */
import React, { RefObject } from 'react';
import { AjaxStore, AjaxStoreConfig, AlignSpec, Base, Button, CellCopyPaste, CellCopyPasteConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, CheckColumnConfig, Column, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnLines, ColumnLinesConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, Combo, Container, ContainerItemConfig, ContainerLayoutConfig, CrudManager, CrudManagerConfig, DateField, DependencyBaseModel, DependencyStoreConfig, DisplayField, DomClassList, DomConfig, DragHelper, DropData, DurationField, Editor, EventColor, EventCopyPaste, EventCopyPasteConfig, EventDrag, EventDragConfig, EventDragCreate, EventDragCreateConfig, EventDragSelect, EventDragSelectConfig, EventEdit, EventEditConfig, EventFilter, EventFilterConfig, EventMenu, EventMenuConfig, EventNonWorkingTime, EventNonWorkingTimeConfig, EventTooltip, EventTooltipConfig, ExcelExporter, ExcelExporterConfig, Field, FileDrop, FileDropConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, Grid, GridBase, GridCellEdit, GridCellEditConfig, GridColumnConfig, Group, GroupConfig, GroupSummary, GroupSummaryConfig, HeaderMenu, HeaderMenuConfig, HeaderZoom, HeaderZoomConfig, Labels, LabelsConfig, Layout, Location, Mask, MaskConfig, Menu, MenuItem, MenuItemConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, NonWorkingTime, NonWorkingTimeConfig, Pan, PanConfig, Panel, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, PdfExport, PdfExportConfig, Popup, PreserveScrollOptions, PresetStore, Print, PrintConfig, ProjectModel, ProjectModelConfig, QuickFind, QuickFindConfig, RecordPositionContext, Rectangle, RegionResize, RegionResizeConfig, ResourceHeaderConfig, ResourceMarginConfig, ResourceMenu, ResourceMenuConfig, ResourceTimeRangeModel, ResourceTimeRangeModelConfig, ResourceTimeRangeStore, ResourceTimeRangeStoreConfig, ResourceTimeRanges, ResourceTimeRangesConfig, Row, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowNumberColumnConfig, RowReorder, RowReorderConfig, RowResize, RowResizeConfig, ScheduleContext, ScheduleContextConfig, ScheduleMenu, ScheduleMenuConfig, ScheduleTooltip, ScheduleTooltipConfig, Scheduler, SchedulerAssignmentModel, SchedulerAssignmentModelConfig, SchedulerAssignmentStore, SchedulerAssignmentStoreConfig, SchedulerDependencies, SchedulerDependenciesConfig, SchedulerDependencyEdit, SchedulerDependencyEditConfig, SchedulerDependencyModel, SchedulerDependencyModelConfig, SchedulerDependencyStore, SchedulerEventModel, SchedulerEventModelConfig, SchedulerEventResize, SchedulerEventResizeConfig, SchedulerEventStore, SchedulerEventStoreConfig, SchedulerExportDialog, SchedulerFeaturesConfigType, SchedulerResourceModel, SchedulerResourceModelConfig, SchedulerResourceStore, SchedulerResourceStoreConfig, ScrollManager, ScrollManagerConfig, Scroller, ScrollerConfig, Search, SearchConfig, SimpleEventEdit, SimpleEventEditConfig, Sort, SortConfig, Split, SplitConfig, StateProvider, StickyCells, StickyCellsConfig, StickyEvents, StickyEventsConfig, Store, StoreConfig, StoreSearchResult, Stripe, StripeConfig, SubGrid, SubGridConfig, Summary, SummaryConfig, TabConfig, TextField, TimeAxis, TimeAxisColumn, TimeAxisConfig, TimeAxisHeaderMenu, TimeAxisHeaderMenuConfig, TimeField, TimeRanges, TimeRangesConfig, TimeSelection, TimeSelectionConfig, TimeSpan, TimeSpanConfig, TimelineBase, TimelineContext, Tool, ToolConfig, ToolbarConfig, Tooltip, TooltipConfig, Tree, TreeConfig, TreeGroup, TreeGroupConfig, VerticalTimeAxisColumn, VerticalTimeAxisColumnConfig, ViewPreset, ViewPresetConfig, VisibleDate, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumSchedulerProps =  {
     // Configs only
    adopt?                            : HTMLElement|string
    align?                            : AlignSpec|string
    allowCreate?                      : boolean
    allowOver?                        : boolean
    anchor?                           : boolean
    anchorToTarget?                   : boolean
    animateRemovingRows?              : boolean
    ariaDescription?                  : string
    ariaLabel?                        : string
    autoAdjustTimeAxis?               : boolean
    autoClose?                        : boolean
    autoHeight?                       : boolean
    autoHide?                         : boolean
    autoShow?                         : boolean
    autoUpdateRecord?                 : boolean
    bbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                          : string|object
    bubbleEvents?                     : object
    bufferCoef?                       : number
    bufferThreshold?                  : number
    callOnFunctions?                  : boolean
    centered?                         : boolean
    closable?                         : boolean
    closeAction?                      : 'hide'|'destroy'
    collapsed?                        : boolean
    collapsible?                      : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    columns?                          : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                           : object
    constrainTo?                      : HTMLElement|Widget|Rectangle
    contentElementCls?                : string|object
    contextMenuTriggerEvent?          : 'contextmenu'|'click'|'dblclick'
    createEventOnDblClick?            : boolean|object
    creationTooltip?                  : Partial<TooltipConfig>
    crudManagerClass?                 : typeof CrudManager
    date?                             : Date
    defaultBindProperty?              : string
    defaultFocus?                     : (widget: Widget) => boolean
    defaultRegion?                    : string
    defaultResourceImageName?         : string
    defaults?                         : object
    descriptionRenderer?              : (view: Widget) => string
    deselectAllOnScheduleClick?       : boolean
    deselectOnClick?                  : boolean
    destroyStore?                     : boolean
    destroyStores?                    : boolean
    disableGridRowModelWarning?       : boolean
    dismissDelay?                     : number
    dock?                             : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                        : boolean|{
        handleSelector?: string
    }
    durationDisplayPrecision?         : number|boolean
    enableDeleteKey?                  : boolean
    enableRecurringEvents?            : boolean
    enableSticky?                     : boolean
    enableTextSelection?              : boolean
    endParamName?                     : string
    eventBarTextField?                : string
    eventBodyTemplate?                : (data: any) => DomConfig|string|void
    eventRenderer?                    : (detail: { eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, renderData: { event: SchedulerEventModel, cls: DomClassList|string, wrapperCls: DomClassList|string, iconCls: DomClassList|string, left: number, width: number, height: number, style: string|Record<string, string>, wrapperStyle: string|Record<string, string>, eventStyle: string, eventColor: string, children: DomConfig[] } }) => string|object|DomConfig|DomConfig[]
    eventRendererThisObj?             : object
    eventSelectionDisabled?           : boolean
    features?                         : Partial<SchedulerFeaturesConfigType>
    fillLastColumn?                   : boolean
    fixedRowHeight?                   : boolean
    floating?                         : boolean
    focusOnToFront?                   : boolean
    footer?                           : {
        dock?: 'top'|'right'|'bottom'|'left'|'start'|'end'
        html?: string
        cls?: string
    }|string
    forElement?                       : HTMLElement
    forSelector?                      : string
    fullRowRefresh?                   : boolean
    getDateConstraints?               : (resourceRecord?: SchedulerResourceModel, eventRecord?: SchedulerEventModel) => object
    getHtml?                          : ((context: { tip: Tooltip, element: HTMLElement, activeTarget: HTMLElement, event: Event }) => string|Promise<any>)|string
    getRowHeight?                     : (getRowHeight: { record: SchedulerResourceModel }) => number
    header?                           : string|boolean|PanelHeader
    hideAnimation?                    : boolean|object
    hideDelay?                        : number|boolean
    hideHorizontalScrollbar?          : boolean
    hideOnDelegateChange?             : boolean
    hideWhenEmpty?                    : boolean
    highlightPredecessors?            : boolean
    highlightSuccessors?              : boolean
    horizontalEventSorterFn?          : () => void
    hoverDelay?                       : number
    htmlCls?                          : string|object
    ignoreDomEventsWhileScrolling?    : boolean
    ignoreParentReadOnly?             : boolean
    infiniteScroll?                   : boolean
    itemCls?                          : string
    keyMap?                           : Record<string, string>
    lazyItems?                        : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                        : object
    loadingMsg?                       : string
    loadMask?                         : string|Partial<MaskConfig>|null
    loadMaskDefaults?                 : Partial<MaskConfig>
    loadMaskError?                    : Partial<MaskConfig>|Mask|boolean
    localeClass?                      : typeof Base
    localizable?                      : boolean
    localizableProperties?            : string[]
    maintainSelectionOnDatasetChange? : boolean
    managedEventSizing?               : boolean
    maskDefaults?                     : Partial<MaskConfig>
    masked?                           : boolean|string|Partial<MaskConfig>
    maximizable?                      : boolean
    maximized?                        : boolean
    maxTimeAxisUnit?                  : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    modal?                            : boolean|{
        closeOnMaskTap?: boolean
        transparent?: boolean
    }
    mode?                             : 'horizontal'|'vertical'
    monitorResize?                    : boolean
    mouseOffsetX?                     : number
    mouseOffsetY?                     : number
    multiEventSelect?                 : boolean
    namedItems?                       : Record<string, Partial<ContainerItemConfig>>
    narrowEventWidth?                 : number
    owner?                            : Widget
    partner?                          : TimelineBase
    passStartEndParameters?           : boolean
    plugins?                          : Function[]
    positioned?                       : boolean
    preCalculateHeightLimit?          : number
    preserveFocusOnDatasetChange?     : boolean
    preserveScrollOnDatasetChange?    : boolean
    preventTooltipOnTouch?            : boolean
    range?                            : 'day'|'week'|'month'
    relayStoreEvents?                 : boolean
    resizeToFitIncludesHeader?        : boolean
    resourceColumns?                  : Partial<ResourceHeaderConfig>
    resourceImageExtension?           : string
    resourceImagePath?                : string
    responsiveLevels?                 : Record<string, number|string>
    ripple?                           : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?                      : ShadowRoot
    scrollAction?                     : 'hide'|'realign'|null
    scrollerClass?                    : typeof Scroller
    scrollManager?                    : Partial<ScrollManagerConfig>|ScrollManager
    selectionMode?                    : object
    showAnimation?                    : boolean|object
    showCreationTooltip?              : boolean
    showDirty?                        : boolean|{
        duringEdit?: boolean
    }
    showEventColorPickers?            : boolean
    showLagInTooltip?                 : boolean
    showOnClick?                      : boolean
    showOnHover?                      : boolean
    showRecurringUI?                  : boolean
    showTooltip?                      : boolean
    showTooltipWhenDisabled?          : boolean
    snapRelativeToEventStartDate?     : boolean
    startParamName?                   : string
    stateful?                         : boolean|object|string[]
    statefulEvents?                   : object|string[]
    stateId?                          : string
    stateProvider?                    : StateProvider
    stepUnit?                         : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    stickyHeaders?                    : boolean
    strips?                           : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                   : Record<string, Partial<SubGridConfig>>
    suppressFit?                      : boolean
    syncMask?                         : string|Partial<MaskConfig>|null
    tab?                              : boolean|Partial<TabConfig>
    tag?                              : string
    tbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    terminalCls?                      : string
    terminalSides?                    : string[]
    textAlign?                        : 'left'|'center'|'right'|'start'|'end'
    textContent?                      : boolean
    timeAxis?                         : Partial<TimeAxisConfig>|TimeAxis
    trackMouse?                       : boolean
    trapFocus?                        : boolean
    triggerSelectionChangeOnRemove?   : boolean
    ui?                               : string|object
    verticalTimeAxisColumn?           : Partial<VerticalTimeAxisColumnConfig>
    visibleZoomFactor?                : number
    weekStartDay?                     : number
    weight?                           : number
    zoomKeepsOriginalTimespan?        : boolean
    zoomOnMouseWheel?                 : boolean
    zoomOnTimeAxisDoubleClick?        : boolean

     // Configs and properties
    alignSelf?                     : string
    allowDropOnEventBar?           : boolean
    allowOverlap?                  : boolean
    animateTreeNodeToggle?         : boolean
    appendTo?                      : HTMLElement|string
    assignments?                   : SchedulerAssignmentModel[]|object[]|Partial<SchedulerAssignmentModelConfig>[]
    assignmentStore?               : SchedulerAssignmentStore|Partial<SchedulerAssignmentStoreConfig>
    barMargin?                     : number
    cls?                           : string|object
    columnLines?                   : boolean
    content?                       : string
    creationTooltipTemplate?       : Function|((data: { source: TimeSpan, target: TimeSpan, fromSide: string, toSide: string, valid: boolean }) => string|DomConfig)
    crudManager?                   : CrudManager|Partial<CrudManagerConfig>
    data?                          : object[]|Model[]|Partial<ModelConfig>[]
    dataset?                       : object
    dateFormat?                    : string
    dateSeparator?                 : string
    dependencies?                  : SchedulerDependencyModel[]|Partial<SchedulerDependencyModelConfig>[]
    dependencyStore?               : SchedulerDependencyStore|Partial<DependencyStoreConfig>
    descriptionFormat?             : string|string[]|boolean[]
    disabled?                      : boolean
    displayDateFormat?             : string
    emptyText?                     : string
    enableEventAnimations?         : boolean
    enableUndoRedoKeys?            : boolean
    endDate?                       : Date|string
    eventColor?                    : EventColor
    eventLayout?                   : 'stack'|'pack'|'mixed'|'none'|object
    events?                        : SchedulerEventModel[]|Partial<SchedulerEventModelConfig>[]
    eventStore?                    : SchedulerEventStore|Partial<SchedulerEventStoreConfig>
    eventStyle?                    : 'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null
    extraData?                     : string|number|boolean|object
    fillTicks?                     : boolean
    flex?                          : number|string
    forceFit?                      : boolean
    height?                        : number|string
    hidden?                        : boolean
    hideFooters?                   : boolean
    hideHeaders?                   : boolean
    hideRowHover?                  : boolean
    html?                          : string|((widget: Widget) => string)
    id?                            : string
    insertBefore?                  : HTMLElement|string
    insertFirst?                   : HTMLElement|string
    isEventSelectable?             : Function|((event: SchedulerEventModel) => boolean)
    items?                         : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?                        : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?                   : object
    longPressTime?                 : number
    margin?                        : number|string
    maxHeight?                     : string|number
    maximizeOnMobile?              : number|string
    maxWidth?                      : string|number
    maxZoomLevel?                  : number
    milestoneAlign?                : 'start'|'center'|'end'
    milestoneCharWidth?            : number
    milestoneLayoutMode?           : 'default'|'estimate'|'data'|'measure'
    milestoneTextPosition?         : 'inside'|'outside'|'always-outside'
    minHeight?                     : string|number
    minWidth?                      : string|number
    minZoomLevel?                  : number
    overlappingEventSorter?        : Function|((a: SchedulerEventModel, b: SchedulerEventModel) => number)
    preserveScroll?                : PreserveScrollOptions|boolean
    presets?                       : PresetStore|Partial<ViewPresetConfig>[]
    project?                       : Partial<ProjectModel|ProjectModelConfig>|RefObject<any>
    readOnly?                      : boolean
    record?                        : Model
    resourceMargin?                : number|Partial<ResourceMarginConfig>
    resources?                     : SchedulerResourceModel[]|Partial<SchedulerResourceModelConfig>[]
    resourceStore?                 : SchedulerResourceStore|Partial<SchedulerResourceStoreConfig>
    resourceTimeRanges?            : ResourceTimeRangeModel[]|Partial<ResourceTimeRangeModelConfig>[]
    resourceTimeRangeStore?        : ResourceTimeRangeStore|Partial<ResourceTimeRangeStoreConfig>
    rowHeight?                     : number
    rowLines?                      : boolean
    scrollable?                    : Scroller|boolean|Partial<ScrollerConfig>
    selectResourceOnEventNavigate? : boolean
    selectResourceOnScheduleClick? : boolean
    snap?                          : boolean
    startDate?                     : Date|string
    store?                         : Partial<Store|StoreConfig|AjaxStore|AjaxStoreConfig>
    strictRecordMapping?           : boolean
    terminalHideDelay?             : number
    terminalOffset?                : number
    terminalShowDelay?             : number
    terminalSize?                  : number|string
    tickSize?                      : number
    timeRanges?                    : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeRangeStore?                : Store|Partial<StoreConfig>
    timeZone?                      : string|number
    title?                         : string
    tools?                         : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?                       : string|Partial<TooltipConfig>
    tooltipTemplate?               : Function|((dependency: DependencyBaseModel) => string|DomConfig)
    transitionDuration?            : number
    updateTimelineContextOnScroll? : boolean
    useInitialAnimation?           : boolean|'fade-in'|'slide-from-left'|'slide-from-top'|string
    viewPreset?                    : ViewPreset|Partial<ViewPresetConfig>|string
    visibleDate?                   : object|Date|VisibleDate
    width?                         : number|string
    workingTime?                   : object
    x?                             : number
    y?                             : number

     // Properties only
    anchorSize?          : number[]
    eventColors?         : string[]
    eventStyles?         : string[]
    isEngineReady?       : boolean
    isSettingValues?     : boolean
    isValid?             : boolean
    scrollLeft?          : number
    scrollTop?           : number
    scrollX?             : number
    selectedAssignments? : SchedulerAssignmentModel[]
    selectedCell?        : Location
    selectedCells?       : Location[]
    selectedEvents?      : SchedulerEventModel[]
    selectedRecord?      : Model
    selectedRecords?     : Model[]|number[]
    selectedRows?        : Model[]|number[]
    state?               : object
    timeResolution?      : object|number
    type?                : string
    values?              : Record<string, object>
    zoomLevel?           : number

     // Events
    onAfterDependencyCreateDrop?       : ((event: { source: TimeSpan, target: TimeSpan, dependency: DependencyBaseModel }) => void)|string
    onAfterDependencySave?             : ((event: { source: Scheduler, dependencyRecord: SchedulerDependencyModel }) => void)|string
    onAfterDragCreate?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventElement: HTMLElement }) => void)|string
    onAfterEventDrop?                  : ((event: { source: Scheduler, assignmentRecords: SchedulerAssignmentModel[], eventRecords: SchedulerEventModel[], valid: boolean, context: object, domEvent: MouseEvent }) => void)|string
    onAfterEventEdit?                  : ((event: { source: Scheduler }) => void)|string
    onAfterEventSave?                  : ((event: { source: Scheduler, eventRecord: SchedulerEventModel }) => void)|string
    onAssignmentSelectionChange?       : ((event: { action: 'select'|'deselect'|'update'|'clear', selected: SchedulerAssignmentModel[], deselected: SchedulerAssignmentModel[], selection: SchedulerAssignmentModel[] }) => void)|string
    onBeforeAssignmentDelete?          : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], context: { finalize: { removeRecords?: boolean } } }) => boolean|void)|string
    onBeforeAssignmentSelectionChange? : ((event: { action: string, selected: SchedulerEventModel[], deselected: SchedulerEventModel[], selection: SchedulerEventModel[] }) => boolean|void)|string
    onBeforeCancelCellEdit?            : ((event: { source: Grid, editorContext: Location }) => boolean|void)|string
    onBeforeCellDelete?                : ((event: { source: Grid, gridSelection: (Location|Model)[] }) => boolean|void)|string
    onBeforeCellEditStart?             : ((event: { source: Grid, editorContext: { column: Column, record: Model, cell: HTMLElement, editor: Field, finalize: { context?: object }, value: any } }) => boolean|void)|string
    onBeforeClose?                     : ((event: { source: Popup }) => void)|string
    onBeforeColumnDragStart?           : ((event: { source: Grid, column: Column, event: Event }) => boolean|void)|string
    onBeforeColumnDropFinalize?        : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, event: Event, region: string }) => boolean|void)|string
    onBeforeCopy?                      : ((event: { source: Grid, cells: Location[], data: string, isCut: boolean, entityName: string }) => Promise<boolean|void>)|string
    onBeforeDependencyAdd?             : ((event: { source: Scheduler, dependencyEdit: SchedulerDependencyEdit, dependencyRecord: SchedulerDependencyModel }) => boolean|void)|string
    onBeforeDependencyCreateDrag?      : ((event: { source: TimeSpan }) => void)|string
    onBeforeDependencyCreateFinalize?  : ((event: { source: TimeSpan, target: TimeSpan, fromSide: 'start'|'end'|'top'|'bottom', toSide: 'start'|'end'|'top'|'bottom' }) => Promise<boolean|void>)|string
    onBeforeDependencyDelete?          : ((event: { source: Scheduler, dependencyRecord: SchedulerDependencyModel }) => boolean|void)|string
    onBeforeDependencyEdit?            : ((event: { source: Scheduler, dependencyEdit: SchedulerDependencyEdit, dependencyRecord: SchedulerDependencyModel }) => Promise<boolean|void>)|string
    onBeforeDependencyEditShow?        : ((event: { source: Scheduler, dependencyEdit: SchedulerDependencyEdit, dependencyRecord: SchedulerDependencyModel, editor: Popup }) => void)|string
    onBeforeDependencySave?            : ((event: { source: Scheduler, dependencyRecord: SchedulerDependencyModel, values: object }) => boolean|void)|string
    onBeforeDestroy?                   : ((event: { source: Base }) => void)|string
    onBeforeDragCreate?                : ((event: { source: Scheduler, resourceRecord: SchedulerResourceModel, date: Date }) => boolean|void)|string
    onBeforeDragCreateFinalize?        : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventElement: HTMLElement, context: { async: boolean, finalize: Function } }) => void)|string
    onBeforeEventAdd?                  : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecords: SchedulerResourceModel[], assignmentRecords: SchedulerAssignmentModel[] }) => boolean|void)|string
    onBeforeEventDelete?               : ((event: { source: Scheduler|any, eventRecords: SchedulerEventModel[], context: { finalize: { removeRecords?: boolean } } }) => Promise<boolean|void>)|string
    onBeforeEventDrag?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], event: MouseEvent, domEvent: MouseEvent }) => boolean|void)|string
    onBeforeEventDragSelect?           : ((event: { event: Event }) => boolean|void)|string
    onBeforeEventDropFinalize?         : ((event: { source: Scheduler, context: { dropData: DropData, async: boolean, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], targetEventRecord: SchedulerEventModel, newResource: SchedulerResourceModel, valid: boolean, finalize: Function }, domEvent: MouseEvent }) => void)|string
    onBeforeEventEdit?                 : ((event: { source: Scheduler, eventEdit: EventEdit, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventElement: HTMLElement }) => boolean|void)|string
    onBeforeEventEditShow?             : ((event: { source: Scheduler, eventEdit: EventEdit, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventElement: HTMLElement, editor: Popup }) => void)|string
    onBeforeEventResize?               : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => boolean|void)|string
    onBeforeEventResizeFinalize?       : ((event: { source: Scheduler, context: { eventRecord: SchedulerEventModel, startDate: Date, endDate: Date, originalStartDate: Date, originalEndDate: Date, async: boolean, finalize: Function }, event: Event }) => void)|string
    onBeforeEventSave?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecords: SchedulerResourceModel[], values: object, context: { async?: boolean, finalize: Function } }) => Promise<boolean|void>)|string
    onBeforeEventSelectionChange?      : ((event: { action: string, selected: SchedulerEventModel[], deselected: SchedulerEventModel[], selection: SchedulerEventModel[] }) => boolean|void)|string
    onBeforeFinishCellEdit?            : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onBeforeHide?                      : ((event: { source: Widget }) => boolean|void)|string
    onBeforePan?                       : ((event: { event: Event }) => boolean|void)|string
    onBeforePaste?                     : ((event: { source: Grid, clipboardData: string, targetCell: Location, entityName: string }) => Promise<boolean|void>)|string
    onBeforePdfExport?                 : ((event: { config: object }) => boolean|void)|string
    onBeforePresetChange?              : ((event: { source: Scheduler, startDate: Date, endDate: Date, from: ViewPreset, to: ViewPreset }) => boolean|void)|string
    onBeforeRenderRow?                 : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onBeforeRenderRows?                : ((event: { source: Grid }) => void)|string
    onBeforeSelectionChange?           : ((event: { action: string, mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => boolean|void)|string
    onBeforeSetRecord?                 : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?                      : ((event: { source: { triggeredByEvent: Event } }) => Promise<boolean|void>)|string
    onBeforeShowTerminals?             : ((event: { source: TimeSpan }) => void)|string
    onBeforeToggleGroup?               : ((event: { groupRecord: Model, collapse: boolean, domEvent: Event }) => boolean|void)|string
    onBeforeToggleNode?                : ((event: { source: Grid, record: Model, collapse: boolean }) => void)|string
    onCancelCellEdit?                  : ((event: { source: Grid, editorContext: Location, event: Event }) => void)|string
    onCatchAll?                        : ((event: {[key: string]: any, type: string}) => void)|string
    onCellClick?                       : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellContextMenu?                 : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellDblClick?                    : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMenuBeforeShow?              : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => boolean|void)|string
    onCellMenuItem?                    : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model }) => void)|string
    onCellMenuShow?                    : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => void)|string
    onCellMenuToggleItem?              : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model, checked: boolean }) => void)|string
    onCellMouseOut?                    : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMouseOver?                   : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCollapse?                        : ((event: { source: Panel }) => void)|string
    onCollapseNode?                    : ((event: { source: Grid, record: Model }) => void)|string
    onColumnDrag?                      : ((event: { source: Grid, column: Column, insertBefore: Column, event: Event, context: { valid: boolean } }) => void)|string
    onColumnDragStart?                 : ((event: { source: Grid, column: Column, event: Event }) => void)|string
    onColumnDrop?                      : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, valid: boolean, event: Event, region: string }) => boolean|void)|string
    onContextMenuItem?                 : ((event: { source: Widget, menu: Menu, item: MenuItem }) => void)|string
    onContextMenuToggleItem?           : ((event: { source: Widget, menu: Menu, item: MenuItem, checked: boolean }) => void)|string
    onCopy?                            : ((event: { source: Grid, cells: Location[], copiedDataString: string, isCut: boolean, entityName: string }) => void)|string
    onDataChange?                      : ((event: { source: Grid, store: Store, action: 'remove'|'removeAll'|'add'|'updatemultiple'|'clearchanges'|'filter'|'update'|'dataset'|'replace', record: Model, records: Model[], changes: object }) => void)|string
    onDateRangeChange?                 : ((event: { source: TimelineBase, old: { startDate: Date, endDate: Date }, new: { startDate: Date, endDate: Date } }) => void)|string
    onDependenciesDrawn?               : (() => void)|string
    onDependencyClick?                 : ((event: { source: Scheduler, dependency: SchedulerDependencyModel, event: MouseEvent }) => void)|string
    onDependencyContextMenu?           : ((event: { source: Scheduler, dependency: SchedulerDependencyModel, event: MouseEvent }) => void)|string
    onDependencyCreateDragStart?       : ((event: { source: TimeSpan }) => void)|string
    onDependencyCreateDrop?            : ((event: { source: TimeSpan, target: TimeSpan, dependency: DependencyBaseModel }) => void)|string
    onDependencyDblClick?              : ((event: { source: Scheduler, dependency: SchedulerDependencyModel, event: MouseEvent }) => void)|string
    onDependencyMouseOut?              : ((event: { source: Scheduler, dependency: SchedulerDependencyModel, event: MouseEvent }) => void)|string
    onDependencyMouseOver?             : ((event: { source: Scheduler, dependency: SchedulerDependencyModel, event: MouseEvent }) => void)|string
    onDependencyValidationComplete?    : ((event: { source: TimeSpan, target: TimeSpan, dependencyType: number }) => void)|string
    onDependencyValidationStart?       : ((event: { source: TimeSpan, target: TimeSpan, dependencyType: number }) => void)|string
    onDestroy?                         : ((event: { source: Base }) => void)|string
    onDragCreateEnd?                   : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, event: MouseEvent, eventElement: HTMLElement }) => void)|string
    onDragCreateStart?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, eventElement: HTMLElement }) => void)|string
    onDragSelecting?                   : ((event: { source: Grid, selectedCells: Model[]|object|Partial<ModelConfig> }) => void)|string
    onEventAutoCreated?                : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel }) => void)|string
    onEventClick?                      : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventContextMenu?                : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventDblClick?                   : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventDrag?                       : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], startDate: Date, endDate: Date, resourceRecord: SchedulerResourceModel, newResource: SchedulerResourceModel, context: { valid: boolean }, domEvent: MouseEvent }) => void)|string
    onEventDragAbort?                  : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], domEvent: MouseEvent }) => void)|string
    onEventDragModeChange?             : ((event: { mode: string }) => void)|string
    onEventDragReset?                  : ((event: { source: Scheduler }) => void)|string
    onEventDragStart?                  : ((event: { source: Scheduler, resourceRecord: SchedulerResourceModel, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onEventDrop?                       : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], externalDropTarget: HTMLElement, isCopy: boolean, context: { targetEventRecord: SchedulerEventModel, newResource: SchedulerResourceModel }, domEvent: MouseEvent }) => void)|string
    onEventEditBeforeSetRecord?        : ((event: { source: Container, record: SchedulerEventModel }) => void)|string
    onEventKeyDown?                    : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], event: KeyboardEvent }) => void)|string
    onEventKeyUp?                      : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], event: KeyboardEvent }) => void)|string
    onEventMenuBeforeShow?             : ((event: { source: Scheduler, items: Record<string, Partial<MenuItemConfig>>, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, eventElement: HTMLElement, event?: MouseEvent }) => boolean|void)|string
    onEventMenuItem?                   : ((event: { source: Scheduler, item: MenuItem, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, eventElement: HTMLElement }) => void)|string
    onEventMenuShow?                   : ((event: { source: Scheduler, menu: Menu, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, eventElement: HTMLElement }) => void)|string
    onEventMouseDown?                  : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventMouseEnter?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventMouseLeave?                 : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventMouseOut?                   : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventMouseOver?                  : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventMouseUp?                    : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, assignmentRecord: SchedulerAssignmentModel, event: MouseEvent }) => void)|string
    onEventPartialResize?              : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, startDate: Date, endDate: Date, element: HTMLElement }) => void)|string
    onEventResizeEnd?                  : ((event: { source: Scheduler, changed: boolean, eventRecord: SchedulerEventModel }) => void)|string
    onEventResizeStart?                : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onEventSelectionChange?            : ((event: { action: 'select'|'deselect'|'update'|'clear', selected: SchedulerEventModel[], deselected: SchedulerEventModel[], selection: SchedulerEventModel[] }) => void)|string
    onExpand?                          : ((event: { source: Panel }) => void)|string
    onExpandNode?                      : ((event: { source: Grid, record: Model }) => void)|string
    onFileDrop?                        : ((event: { source: Grid, file: DataTransferItem, domEvent: DragEvent }) => void)|string
    onFinishCellEdit?                  : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onFocusIn?                         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?                        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onGridRowAbort?                    : ((event: { source: DragHelper, context: object, event: MouseEvent }) => void)|string
    onGridRowBeforeDragStart?          : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => boolean|void)|string
    onGridRowBeforeDropFinalize?       : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => Promise<boolean|void>)|string
    onGridRowDrag?                     : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[] }, event: MouseEvent }) => void)|string
    onGridRowDragStart?                : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => void)|string
    onGridRowDrop?                     : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, record: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => void)|string
    onHeaderMenuBeforeShow?            : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => boolean|void)|string
    onHeaderMenuItem?                  : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column }) => void)|string
    onHeaderMenuShow?                  : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => void)|string
    onHeaderMenuToggleItem?            : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, checked: boolean }) => void)|string
    onHide?                            : ((event: { source: Widget }) => void)|string
    onMouseOut?                        : ((event: { event: MouseEvent }) => void)|string
    onMouseOver?                       : ((event: { event: MouseEvent }) => void)|string
    onNavigate?                        : ((event: { event: Event, item: HTMLElement|null, oldItem: HTMLElement|null }) => void)|string
    onPaint?                           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onPaste?                           : ((event: { source: Grid, clipboardData: string, modifiedRecords: Model[], targetCell: Location, entityName: string }) => void)|string
    onPasteNotAllowed?                 : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], assignmentRecords: SchedulerAssignmentModel[], date: Date, resourceRecord: SchedulerResourceModel, isCut: boolean, entityName: string, reason: 'overlappingEvents'|'resourceReadOnly' }) => void)|string
    onPdfExport?                       : ((event: { response?: any, error?: Error }) => void)|string
    onPointerOver?                     : ((event: { sourceThe: Tooltip, event: Event }) => void)|string
    onPresetChange?                    : ((event: { source: Scheduler, startDate: Date, centerDate: Date, endDate: Date, from: ViewPreset, to: ViewPreset }) => boolean|void)|string
    onReadOnly?                        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?                       : (() => void)|string
    onReleaseEvent?                    : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, renderData: object, element: HTMLElement }) => void)|string
    onRenderEvent?                     : ((event: { source: Scheduler, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, assignmentRecord: SchedulerAssignmentModel, renderData: object, isRepaint: boolean, isReusingElement: boolean, element: HTMLElement }) => void)|string
    onRenderRow?                       : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onRenderRows?                      : ((event: { source: Grid }) => void)|string
    onResize?                          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onResourceHeaderClick?             : ((event: { source: Scheduler, resourceRecord: SchedulerResourceModel, event: Event }) => void)|string
    onResourceHeaderContextmenu?       : ((event: { source: Scheduler, resourceRecord: SchedulerResourceModel, event: Event }) => void)|string
    onResourceHeaderDblclick?          : ((event: { source: Scheduler, resourceRecord: SchedulerResourceModel, event: Event }) => void)|string
    onResourceMenuBeforeShow?          : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, resourceRecord: SchedulerResourceModel }) => boolean|void)|string
    onResourceMenuItem?                : ((event: { source: Scheduler, menu: Menu, item: MenuItem, record: SchedulerResourceModel }) => void)|string
    onResourceMenuShow?                : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, record: SchedulerResourceModel }) => void)|string
    onResourceMenuToggleItem?          : ((event: { source: Scheduler, menu: Menu, item: MenuItem, resourceRecord: SchedulerResourceModel, checked: boolean }) => void)|string
    onResourceTimeRangeClick?          : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeContextMenu?    : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeDblClick?       : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseDown?      : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseOut?       : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseOver?      : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseUp?        : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResponsive?                      : ((event: { grid: Grid, level: string, width: number, oldLevel: string, oldWidth: number }) => void)|string
    onScheduleClick?                   : ((event: { source: Scheduler, date: Date, tick: TimeSpan, tickIndex: number, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleContextMenu?             : ((event: { source: Scheduler, date: Date, tick: TimeSpan, tickIndex: number, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleDblClick?                : ((event: { source: Scheduler, date: Date, tick: TimeSpan, tickIndex: number, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleMenuBeforeShow?          : ((event: { source: Scheduler, items: Record<string, Partial<MenuItemConfig>>, eventRecord: SchedulerEventModel, resourceRecord: SchedulerResourceModel, date: Date, assignmentRecord: SchedulerAssignmentModel, eventElement: HTMLElement }) => boolean|void)|string
    onScheduleMenuItem?                : ((event: { source: Scheduler, item: MenuItem, resourceRecord: SchedulerResourceModel, date: Date, element: HTMLElement }) => void)|string
    onScheduleMenuShow?                : ((event: { source: Scheduler, items: Record<string, Partial<MenuItemConfig>>, menu: Menu, resourceRecord: SchedulerResourceModel, date: Date, targetElement: HTMLElement }) => void)|string
    onScheduleMouseDown?               : ((event: { source: Scheduler, date: Date, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleMouseEnter?              : ((event: { source: Scheduler, date: Date, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleMouseLeave?              : ((event: { source: Scheduler, event: MouseEvent }) => void)|string
    onScheduleMouseMove?               : ((event: { source: Scheduler, date: Date, tick: TimeSpan, tickIndex: number, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScheduleMouseUp?                 : ((event: { source: Scheduler, date: Date, tickStartDate: Date, tickEndDate: Date, row: Row, index: number, resourceRecord: SchedulerResourceModel, event: MouseEvent }) => void)|string
    onScroll?                          : ((event: { source: Grid, scrollTop: number }) => void)|string
    onSelectionChange?                 : ((event: { action: 'select'|'deselect', mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => void)|string
    onSelectionModeChange?             : ((event: { selectionMode: object }) => void)|string
    onShow?                            : ((event: { source: Widget }) => void)|string
    onSplit?                           : ((event: { subViews: GridBase[], options: { direction: 'horizontal'|'vertical'|'both', atColumn: Column, atRecord: Model } }) => void)|string
    onSplitterCollapseClick?           : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => boolean|void)|string
    onSplitterDragEnd?                 : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onSplitterDragStart?               : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onStartCellEdit?                   : ((event: { source: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onSubGridCollapse?                 : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onSubGridExpand?                   : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onTimeAxisChange?                  : ((event: { source: Scheduler, config: { startDate: Date, endDate: Date } }) => void)|string
    onTimeAxisHeaderClick?             : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderContextMenu?       : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderDblClick?          : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderMenuBeforeShow?    : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => boolean|void)|string
    onTimeAxisHeaderMenuItem?          : ((event: { source: Scheduler, menu: Menu, item: MenuItem, column: Column }) => void)|string
    onTimeAxisHeaderMenuShow?          : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => void)|string
    onTimelineContextChange?           : ((event: { oldContext: TimelineContext, context: TimelineContext }) => void)|string
    onTimelineViewportResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onTimeRangeHeaderClick?            : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onTimeRangeHeaderContextMenu?      : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onTimeRangeHeaderDblClick?         : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onTimeSelectionChange?             : ((event: { source: Scheduler, startDate: Date, endDate: Date }) => void)|string
    onTimeSelectionElementClick?       : ((event: { source: Scheduler, startDate: Date, endDate: Date, domEvent: Event }) => void)|string
    onToggleGroup?                     : ((event: { groupRecord: Model, collapse: boolean }) => void)|string
    onToggleNode?                      : ((event: { record: Model, collapse: boolean }) => void)|string
    onToolClick?                       : ((event: { source: Tool, tool: Tool }) => void)|string
    onUnsplit?                         : (() => void)|string
    onVisibleDateRangeChange?          : ((event: { source: Scheduler, old: { startDate: Date, endDate: Date }, new: { startDate: Date, endDate: Date } }) => void)|string

     // Features
    cellCopyPasteFeature?       : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?            : object|boolean|string|GridCellEdit|Partial<GridCellEditConfig>
    cellMenuFeature?            : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?         : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?     : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature?   : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnLinesFeature?         : object|boolean|string|ColumnLines|Partial<ColumnLinesConfig>
    columnPickerFeature?        : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?        : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?       : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?        : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    dependenciesFeature?        : object|boolean|string|SchedulerDependencies|Partial<SchedulerDependenciesConfig>
    dependencyEditFeature?      : object|boolean|string|SchedulerDependencyEdit|Partial<SchedulerDependencyEditConfig>
    eventCopyPasteFeature?      : object|boolean|string|EventCopyPaste|Partial<EventCopyPasteConfig>
    eventDragFeature?           : object|boolean|string|EventDrag|Partial<EventDragConfig>
    eventDragCreateFeature?     : object|boolean|string|EventDragCreate|Partial<EventDragCreateConfig>
    eventDragSelectFeature?     : object|boolean|string|EventDragSelect|Partial<EventDragSelectConfig>
    eventEditFeature?           : object|boolean|string|EventEdit|Partial<EventEditConfig>
    eventFilterFeature?         : object|boolean|string|EventFilter|Partial<EventFilterConfig>
    eventMenuFeature?           : object|boolean|string|EventMenu|Partial<EventMenuConfig>
    eventNonWorkingTimeFeature? : object|boolean|string|EventNonWorkingTime|Partial<EventNonWorkingTimeConfig>
    eventResizeFeature?         : object|boolean|string|SchedulerEventResize|Partial<SchedulerEventResizeConfig>
    eventTooltipFeature?        : object|boolean|string|EventTooltip|Partial<EventTooltipConfig>
    excelExporterFeature?       : object|boolean|string|ExcelExporter|Partial<ExcelExporterConfig>
    fileDropFeature?            : object|boolean|string|FileDrop|Partial<FileDropConfig>
    fillHandleFeature?          : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?              : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?           : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?               : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?        : object|boolean|string|GroupSummary|Partial<GroupSummaryConfig>
    headerMenuFeature?          : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    headerZoomFeature?          : object|boolean|string|HeaderZoom|Partial<HeaderZoomConfig>
    labelsFeature?              : object|boolean|string|Labels|Partial<LabelsConfig>
    mergeCellsFeature?          : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    nonWorkingTimeFeature?      : object|boolean|string|NonWorkingTime|Partial<NonWorkingTimeConfig>
    panFeature?                 : object|boolean|string|Pan|Partial<PanConfig>
    pdfExportFeature?           : object|boolean|string|PdfExport|Partial<PdfExportConfig>
    printFeature?               : object|boolean|string|Print|Partial<PrintConfig>
    quickFindFeature?           : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?        : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    resourceMenuFeature?        : object|boolean|string|ResourceMenu|Partial<ResourceMenuConfig>
    resourceTimeRangesFeature?  : object|boolean|string|ResourceTimeRanges|Partial<ResourceTimeRangesConfig>
    rowCopyPasteFeature?        : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?         : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?          : object|boolean|string|RowReorder|Partial<RowReorderConfig>
    rowResizeFeature?           : object|boolean|string|RowResize|Partial<RowResizeConfig>
    scheduleContextFeature?     : object|boolean|string|ScheduleContext|Partial<ScheduleContextConfig>
    scheduleMenuFeature?        : object|boolean|string|ScheduleMenu|Partial<ScheduleMenuConfig>
    scheduleTooltipFeature?     : object|boolean|string|ScheduleTooltip|Partial<ScheduleTooltipConfig>
    searchFeature?              : object|boolean|string|Search|Partial<SearchConfig>
    simpleEventEditFeature?     : object|boolean|string|SimpleEventEdit|Partial<SimpleEventEditConfig>
    sortFeature?                : object|boolean|string|Sort|Partial<SortConfig>
    splitFeature?               : object|boolean|string|Split|Partial<SplitConfig>
    stickyCellsFeature?         : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stickyEventsFeature?        : object|boolean|string|StickyEvents|Partial<StickyEventsConfig>
    stripeFeature?              : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?             : object|boolean|string|Summary|Partial<SummaryConfig>
    timeAxisHeaderMenuFeature?  : object|boolean|string|TimeAxisHeaderMenu|Partial<TimeAxisHeaderMenuConfig>
    timeRangesFeature?          : object|boolean|string|TimeRanges|Partial<TimeRangesConfig>
    timeSelectionFeature?       : object|boolean|string|TimeSelection|Partial<TimeSelectionConfig>
    treeFeature?                : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?           : object|boolean|string|TreeGroup|Partial<TreeGroupConfig>

}

export class BryntumScheduler extends React.Component<BryntumSchedulerProps> {

    static instanceClass = Scheduler;

    static instanceName = 'Scheduler';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnLinesFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'dependenciesFeature',
        'dependencyEditFeature',
        'eventCopyPasteFeature',
        'eventDragFeature',
        'eventDragCreateFeature',
        'eventDragSelectFeature',
        'eventEditFeature',
        'eventFilterFeature',
        'eventMenuFeature',
        'eventNonWorkingTimeFeature',
        'eventResizeFeature',
        'eventTooltipFeature',
        'excelExporterFeature',
        'fileDropFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'headerZoomFeature',
        'labelsFeature',
        'mergeCellsFeature',
        'nonWorkingTimeFeature',
        'panFeature',
        'pdfExportFeature',
        'printFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'resourceMenuFeature',
        'resourceTimeRangesFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'rowResizeFeature',
        'scheduleContextFeature',
        'scheduleMenuFeature',
        'scheduleTooltipFeature',
        'searchFeature',
        'simpleEventEditFeature',
        'sortFeature',
        'splitFeature',
        'stickyCellsFeature',
        'stickyEventsFeature',
        'stripeFeature',
        'summaryFeature',
        'timeAxisHeaderMenuFeature',
        'timeRangesFeature',
        'timeSelectionFeature',
        'treeFeature',
        'treeGroupFeature'
    ];

    projectStores = false;

    dataStores = {
        'assignmentStore': 'assignments',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'resourceTimeRangeStore': 'resourceTimeRanges',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adopt',
        'align',
        'allowCreate',
        'allowOver',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoAdjustTimeAxis',
        'autoClose',
        'autoHeight',
        'autoHide',
        'autoShow',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'bufferCoef',
        'bufferThreshold',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'createEventOnDblClick',
        'creationTooltip',
        'crudManagerClass',
        'date',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaultResourceImageName',
        'defaults',
        'descriptionRenderer',
        'deselectAllOnScheduleClick',
        'deselectOnClick',
        'destroyStore',
        'destroyStores',
        'disableGridRowModelWarning',
        'dismissDelay',
        'dock',
        'draggable',
        'durationDisplayPrecision',
        'enableDeleteKey',
        'enableRecurringEvents',
        'enableSticky',
        'enableTextSelection',
        'endParamName',
        'eventBarTextField',
        'eventBodyTemplate',
        'eventRenderer',
        'eventRendererThisObj',
        'eventSelectionDisabled',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getDateConstraints',
        'getHtml',
        'getRowHeight',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHorizontalScrollbar',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'highlightPredecessors',
        'highlightSuccessors',
        'horizontalEventSorterFn',
        'hoverDelay',
        'htmlCls',
        'ignoreDomEventsWhileScrolling',
        'ignoreParentReadOnly',
        'infiniteScroll',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maintainSelectionOnDatasetChange',
        'managedEventSizing',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'maxTimeAxisUnit',
        'modal',
        'mode',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'multiEventSelect',
        'namedItems',
        'narrowEventWidth',
        'owner',
        'partner',
        'passStartEndParameters',
        'plugins',
        'positioned',
        'preCalculateHeightLimit',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'range',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'resourceColumns',
        'resourceImageExtension',
        'resourceImagePath',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'selectionMode',
        'showAnimation',
        'showCreationTooltip',
        'showDirty',
        'showEventColorPickers',
        'showLagInTooltip',
        'showOnClick',
        'showOnHover',
        'showRecurringUI',
        'showTooltip',
        'showTooltipWhenDisabled',
        'snapRelativeToEventStartDate',
        'startParamName',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'stepUnit',
        'stickyHeaders',
        'strips',
        'subGridConfigs',
        'suppressFit',
        'syncMask',
        'tab',
        'tag',
        'tbar',
        'terminalCls',
        'terminalSides',
        'textAlign',
        'textContent',
        'timeAxis',
        'trackMouse',
        'trapFocus',
        'triggerSelectionChangeOnRemove',
        'ui',
        'verticalTimeAxisColumn',
        'visibleZoomFactor',
        'weekStartDay',
        'weight',
        'zoomKeepsOriginalTimespan',
        'zoomOnMouseWheel',
        'zoomOnTimeAxisDoubleClick'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'allowDropOnEventBar',
        'allowOverlap',
        'animateTreeNodeToggle',
        'appendTo',
        'assignments',
        'assignmentStore',
        'barMargin',
        'cls',
        'columnLines',
        'content',
        'creationTooltipTemplate',
        'crudManager',
        'data',
        'dataset',
        'dateFormat',
        'dateSeparator',
        'dependencies',
        'dependencyStore',
        'descriptionFormat',
        'disabled',
        'displayDateFormat',
        'emptyText',
        'enableEventAnimations',
        'enableUndoRedoKeys',
        'endDate',
        'eventColor',
        'eventLayout',
        'events',
        'eventStore',
        'eventStyle',
        'extraData',
        'fillTicks',
        'flex',
        'forceFit',
        'height',
        'hidden',
        'hideFooters',
        'hideHeaders',
        'hideRowHover',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'isEventSelectable',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'maxZoomLevel',
        'milestoneAlign',
        'milestoneCharWidth',
        'milestoneLayoutMode',
        'milestoneTextPosition',
        'minHeight',
        'minWidth',
        'minZoomLevel',
        'onAfterDependencyCreateDrop',
        'onAfterDependencySave',
        'onAfterDragCreate',
        'onAfterEventDrop',
        'onAfterEventEdit',
        'onAfterEventSave',
        'onAssignmentSelectionChange',
        'onBeforeAssignmentDelete',
        'onBeforeAssignmentSelectionChange',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDependencyAdd',
        'onBeforeDependencyCreateDrag',
        'onBeforeDependencyCreateFinalize',
        'onBeforeDependencyDelete',
        'onBeforeDependencyEdit',
        'onBeforeDependencyEditShow',
        'onBeforeDependencySave',
        'onBeforeDestroy',
        'onBeforeDragCreate',
        'onBeforeDragCreateFinalize',
        'onBeforeEventAdd',
        'onBeforeEventDelete',
        'onBeforeEventDrag',
        'onBeforeEventDragSelect',
        'onBeforeEventDropFinalize',
        'onBeforeEventEdit',
        'onBeforeEventEditShow',
        'onBeforeEventResize',
        'onBeforeEventResizeFinalize',
        'onBeforeEventSave',
        'onBeforeEventSelectionChange',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforePan',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforePresetChange',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeShowTerminals',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapse',
        'onCollapseNode',
        'onColumnDrag',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDateRangeChange',
        'onDependenciesDrawn',
        'onDependencyClick',
        'onDependencyContextMenu',
        'onDependencyCreateDragStart',
        'onDependencyCreateDrop',
        'onDependencyDblClick',
        'onDependencyMouseOut',
        'onDependencyMouseOver',
        'onDependencyValidationComplete',
        'onDependencyValidationStart',
        'onDestroy',
        'onDragCreateEnd',
        'onDragCreateStart',
        'onDragSelecting',
        'onEventAutoCreated',
        'onEventClick',
        'onEventContextMenu',
        'onEventDblClick',
        'onEventDrag',
        'onEventDragAbort',
        'onEventDragModeChange',
        'onEventDragReset',
        'onEventDragStart',
        'onEventDrop',
        'onEventEditBeforeSetRecord',
        'onEventKeyDown',
        'onEventKeyUp',
        'onEventMenuBeforeShow',
        'onEventMenuItem',
        'onEventMenuShow',
        'onEventMouseDown',
        'onEventMouseEnter',
        'onEventMouseLeave',
        'onEventMouseOut',
        'onEventMouseOver',
        'onEventMouseUp',
        'onEventPartialResize',
        'onEventResizeEnd',
        'onEventResizeStart',
        'onEventSelectionChange',
        'onExpand',
        'onExpandNode',
        'onFileDrop',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onMouseOut',
        'onMouseOver',
        'onNavigate',
        'onPaint',
        'onPaste',
        'onPasteNotAllowed',
        'onPdfExport',
        'onPointerOver',
        'onPresetChange',
        'onReadOnly',
        'onRecompose',
        'onReleaseEvent',
        'onRenderEvent',
        'onRenderRow',
        'onRenderRows',
        'onResize',
        'onResourceHeaderClick',
        'onResourceHeaderContextmenu',
        'onResourceHeaderDblclick',
        'onResourceMenuBeforeShow',
        'onResourceMenuItem',
        'onResourceMenuShow',
        'onResourceMenuToggleItem',
        'onResourceTimeRangeClick',
        'onResourceTimeRangeContextMenu',
        'onResourceTimeRangeDblClick',
        'onResourceTimeRangeMouseDown',
        'onResourceTimeRangeMouseOut',
        'onResourceTimeRangeMouseOver',
        'onResourceTimeRangeMouseUp',
        'onResponsive',
        'onScheduleClick',
        'onScheduleContextMenu',
        'onScheduleDblClick',
        'onScheduleMenuBeforeShow',
        'onScheduleMenuItem',
        'onScheduleMenuShow',
        'onScheduleMouseDown',
        'onScheduleMouseEnter',
        'onScheduleMouseLeave',
        'onScheduleMouseMove',
        'onScheduleMouseUp',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onSplit',
        'onSplitterCollapseClick',
        'onSplitterDragEnd',
        'onSplitterDragStart',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onTimeAxisChange',
        'onTimeAxisHeaderClick',
        'onTimeAxisHeaderContextMenu',
        'onTimeAxisHeaderDblClick',
        'onTimeAxisHeaderMenuBeforeShow',
        'onTimeAxisHeaderMenuItem',
        'onTimeAxisHeaderMenuShow',
        'onTimelineContextChange',
        'onTimelineViewportResize',
        'onTimeRangeHeaderClick',
        'onTimeRangeHeaderContextMenu',
        'onTimeRangeHeaderDblClick',
        'onTimeSelectionChange',
        'onTimeSelectionElementClick',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onUnsplit',
        'onVisibleDateRangeChange',
        'overlappingEventSorter',
        'preserveScroll',
        'presets',
        'project',
        'readOnly',
        'record',
        'resourceMargin',
        'resources',
        'resourceStore',
        'resourceTimeRanges',
        'resourceTimeRangeStore',
        'rowHeight',
        'rowLines',
        'scrollable',
        'selectResourceOnEventNavigate',
        'selectResourceOnScheduleClick',
        'snap',
        'startDate',
        'store',
        'strictRecordMapping',
        'terminalHideDelay',
        'terminalOffset',
        'terminalShowDelay',
        'terminalSize',
        'tickSize',
        'timeRanges',
        'timeRangeStore',
        'timeZone',
        'title',
        'tools',
        'tooltip',
        'tooltipTemplate',
        'transitionDuration',
        'updateTimelineContextOnScroll',
        'useInitialAnimation',
        'viewPreset',
        'visibleDate',
        'width',
        'workingTime',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'eventColors',
        'eventStyles',
        'isEngineReady',
        'isSettingValues',
        'isValid',
        'scrollLeft',
        'scrollTop',
        'scrollX',
        'selectedAssignments',
        'selectedCell',
        'selectedCells',
        'selectedEvents',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'timeResolution',
        'type',
        'values',
        'zoomLevel'
    ];

    // Component instance
    instance!: Scheduler;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumSchedulerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-scheduler-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
